import React, { useState, useEffect } from "react";
import { supabase } from "../../../../src/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Plus, Edit, Trash2, User, Shield, Key, Eye, EyeOff, FileText } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ProjectReportButton } from "@/components/ProjectReportButton";
import { SecurityStatusPanel } from "@/components/admin/SecurityStatusPanel";

interface AdminUser {
  id: string;
  email: string;
  full_name?: string;
  user_name: string;
  role: string;
  region_id?: string;
  is_active: boolean;
  created_at: string;
  regions?: { name: string };
}

interface Region {
  id: string;
  name: string;
}

interface SettingsManagerProps {
  userRole: string;
  userRegion?: string;
}

export const SettingsManager: React.FC<SettingsManagerProps> = ({ userRole, userRegion }) => {
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [loading, setLoading] = useState(true);
  const [isUserDialogOpen, setIsUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [showPassword, setShowPassword] = useState(true); // Default to visible
  const { toast } = useToast();

  const [userFormData, setUserFormData] = useState({
    email: "",
    user_name: "",
    full_name: "",
    role: "regional_admin",
    region_id: "",
    is_active: true,
    password: "",
  });

  const roles = [
    { value: "global_admin", label: "Global Admin" },
    { value: "kit_admin", label: "KIT Admin" },
    { value: "president", label: "President" },
    { value: "regional_admin", label: "Regional Admin" },
  ];

  useEffect(() => {
    fetchAdminUsers();
    fetchRegions();
  }, [userRole, userRegion]);

  const fetchAdminUsers = async () => {
    try {
      let query = supabase.from("admin_users").select("*, regions(name)").order("created_at", { ascending: false });

      // If regional admin, only show users in their region
      if (userRole === "regional_admin" && userRegion) {
        query = query.eq("region_id", userRegion);
      }

      const { data, error } = await query;

      if (error) throw error;
      setAdminUsers(data || []);
    } catch (error) {
      console.error("Error fetching admin users:", error);
      toast({
        title: "Error",
        description: "Failed to fetch admin users",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchRegions = async () => {
    try {
      const { data, error } = await supabase.from("regions").select("id, name").order("name");

      if (error) throw error;
      setRegions(data || []);
    } catch (error) {
      console.error("Error fetching regions:", error);
    }
  };

  const handleUserSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingUser) {
        // Update existing user
        const { error } = await supabase
          .from("admin_users")
          .update({
            email: userFormData.email,
            full_name: userFormData.full_name,
            user_name: userFormData.user_name,
            role: userFormData.role,
            region_id: userFormData.region_id || null,
            is_active: userFormData.is_active,
          })
          .eq("id", editingUser.id);

        if (error) throw error;
        toast({ title: "Success", description: "Admin user updated successfully" });
      } else {
        // Create new user with Supabase Auth
        if (!userFormData.password || userFormData.password.length < 6) {
          toast({
            title: "Error",
            description: "Password must be at least 6 characters long",
            variant: "destructive",
          });
          setLoading(false);
          return;
        }

        // Call edge function to create admin with Supabase Auth
        const { data, error } = await supabase.functions.invoke("create-admin-user", {
          body: {
            email: userFormData.email,
            password: userFormData.password,
            full_name: userFormData.full_name,
            user_name: userFormData.user_name,
            role: userFormData.role,
            region_id: userFormData.region_id || null,
          },
        });

        if (error) throw error;
        if (!data.success) throw new Error(data.error || "Failed to create admin user");

        toast({
          title: "Success",
          description: "Admin user created successfully. Welcome email sent with login instructions.",
        });
      }

      resetUserForm();
      fetchAdminUsers();
    } catch (error: any) {
      console.error("Error saving user:", error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (
      !confirm(
        "Are you sure you want to delete this admin user? This will also delete their authentication account. This action cannot be undone.",
      )
    ) {
      return;
    }

    try {
      // Call the edge function to delete both admin_users record and auth user
      const { data, error } = await supabase.functions.invoke("delete-admin-user", {
        body: { admin_user_id: userId },
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Admin user and authentication account deleted successfully",
      });

      fetchAdminUsers();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to delete admin user",
        variant: "destructive",
      });
    }
  };

  const resetUserForm = () => {
    setUserFormData({
      user_name: "",
      email: "",
      full_name: "",
      role: "regional_admin",
      region_id: "",
      is_active: true,
      password: "",
    });
    setEditingUser(null);
    setIsUserDialogOpen(false);
  };

  const openEditUserDialog = (user: AdminUser) => {
    setEditingUser(user);
    setUserFormData({
      email: user.email,
      user_name: user.user_name || "",
      full_name: user.full_name || "",
      role: user.role,
      region_id: user.region_id || "",
      is_active: user.is_active,
    });
    setIsUserDialogOpen(true);
  };

  const canManageUsers = userRole === "global_admin" || userRole === "Global Admin";

  if (loading) {
    return <div className="flex items-center justify-center p-8">Loading settings...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="space-y-6">
        {/* Admin User Management */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Admin User Management
              </CardTitle>

              {canManageUsers && (
                <Dialog open={isUserDialogOpen} onOpenChange={setIsUserDialogOpen}>
                  <DialogTrigger asChild>
                    <Button
                      onClick={() => {
                        resetUserForm();
                        setIsUserDialogOpen(true);
                      }}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Admin User
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>{editingUser ? "Edit Admin User" : "Add New Admin User"}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleUserSubmit} className="space-y-4">
                      <div>
                        <Label htmlFor="email">Email Address</Label>
                        <Input
                          id="email"
                          type="email"
                          value={userFormData.email}
                          onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                          placeholder="Email"
                          required
                        />
                        <p className="text-xs text-muted-foreground mt-1">The admin will use this email to log in</p>
                      </div>

                      <div>
                        <Label htmlFor="full_name">Full Name</Label>
                        <Input
                          id="full_name"
                          value={userFormData.full_name}
                          onChange={(e) => setUserFormData({ ...userFormData, full_name: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="user_name">User Name</Label>
                        <Input
                          id="user_name"
                          value={userFormData.user_name}
                          onChange={(e) => setUserFormData({ ...userFormData, user_name: e.target.value })}
                        />
                      </div>

                      <div>
                        <Label htmlFor="role">Role</Label>
                        <Select
                          value={userFormData.role}
                          onValueChange={(value: any) => setUserFormData({ ...userFormData, role: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select role" />
                          </SelectTrigger>
                          <SelectContent>
                            {roles.map((role) => (
                              <SelectItem key={role.value} value={role.value}>
                                {role.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {userFormData.role === "regional_admin" && (
                        <div>
                          <Label htmlFor="region_id">Region</Label>
                          <Select
                            value={userFormData.region_id}
                            onValueChange={(value) => setUserFormData({ ...userFormData, region_id: value })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select region" />
                            </SelectTrigger>
                            <SelectContent>
                              {regions.map((region) => (
                                <SelectItem key={region.id} value={region.id}>
                                  {region.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      {!editingUser && (
                        <div>
                          <Label htmlFor="password">Password *</Label>
                          <div className="relative">
                            <Input
                              id="password"
                              type={showPassword ? "text" : "password"}
                              value={userFormData.password}
                              onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
                              placeholder="Minimum 6 characters"
                              required
                              minLength={6}
                              className="pr-10"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              {showPassword ? (
                                <EyeOff className="h-4 w-4 text-muted-foreground" />
                              ) : (
                                <Eye className="h-4 w-4 text-muted-foreground" />
                              )}
                            </Button>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            Admin will use this password to login with their email
                          </p>
                        </div>
                      )}

                      <div className="flex items-center space-x-2">
                        <Switch
                          id="is_active"
                          checked={userFormData.is_active}
                          onCheckedChange={(checked) => setUserFormData({ ...userFormData, is_active: checked })}
                        />
                        <Label htmlFor="is_active">Active</Label>
                      </div>

                      <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" onClick={resetUserForm}>
                          Cancel
                        </Button>
                        <Button type="submit">{editingUser ? "Update" : "Create"}</Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {adminUsers.map((user) => (
                <div key={user.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{user.full_name || user.email}</p>
                      <Badge variant={user.role === "global_admin" ? "default" : "secondary"}>
                        {user.role.replace("_", " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                      </Badge>
                      {!user.is_active && <Badge variant="destructive">Inactive</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground">{user.email}</p>
                    {user.regions && <p className="text-sm text-muted-foreground">Region: {user.regions.name}</p>}
                  </div>

                  {canManageUsers && (
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => openEditUserDialog(user)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => handleDeleteUser(user.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}

              {adminUsers.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">No admin users found</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Project Report Generator - Global Admin Only */}
        {canManageUsers && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Project Report Generator
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Generate a comprehensive PowerPoint report with project statistics, cost comparisons, 
                  feature matrices, security analysis, and ROI summary.
                </p>
                <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/50">
                  <div>
                    <p className="font-medium">OFUESI Project Report</p>
                    <p className="text-sm text-muted-foreground">
                      15 slides with OFUESI branding • Cost & Feature Comparison • Security Analysis
                    </p>
                  </div>
                  <ProjectReportButton variant="default" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Security & Health Status - Global Admin Only */}
        {canManageUsers && <SecurityStatusPanel />}


        {/*
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              System Configuration
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <p className="font-medium">Authentication Settings</p>
                  <p className="text-sm text-muted-foreground">
                    Configure authentication providers and security settings
                  </p>
                </div>
                <Button variant="outline" size="sm">
                  Configure
                </Button>
              </div>
              
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <p className="font-medium">Email Configuration</p>
                  <p className="text-sm text-muted-foreground">
                    Set up email templates and SMTP settings
                  </p>
                </div>
                <Button variant="outline" size="sm">
                  Configure
                </Button>
              </div>
              
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <p className="font-medium">Regional Permissions</p>
                  <p className="text-sm text-muted-foreground">
                    Manage what regional admins can access and modify
                  </p>
                </div>
                <Button variant="outline" size="sm">
                  Configure
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        */}

        {/* API Keys & Integrations */}
        {/*
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              API Keys & Integrations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <p className="font-medium">Analytics Integration</p>
                  <p className="text-sm text-muted-foreground">
                    Connected to Lovable Analytics
                  </p>
                </div>
                <Badge variant="default">Active</Badge>
              </div>
              
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <p className="font-medium">Email Service</p>
                  <p className="text-sm text-muted-foreground">
                    Resend API for transactional emails
                  </p>
                </div>
                <Badge variant="default">Active</Badge>
              </div>
              
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <p className="font-medium">AI Proofreading</p>
                  <p className="text-sm text-muted-foreground">
                    OpenAI integration for content enhancement
                  </p>
                </div>
                <Badge variant="default">Active</Badge>
              </div>
            </div>
          </CardContent>
        </Card>
        */}
      </div>
    </div>
  );
};
